import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { apiStateSelectors } from 'modules/api/apiState'
import { walletApiEvents } from 'modules/api/wallet'
import { SUSPENSE_NOTE_MAX_LENGTH } from 'modules/api/wallet/constants'
import { Button } from 'views/components'
import { formatStandard } from 'utils/date'
import styles from './SuspenseNote.module.scss'

interface SuspenseNoteProps {
  txId?: string
  note?: string
  updatedBy?: string
  updatedAt?: string
  /** A one-line input for table rows, or a textarea with a Save note button for the detail panel */
  multiline?: boolean
  placeholder?: string
}

/**
 * In a table row it saves on blur or Enter; in the panel, on Save note or Cmd/Ctrl+Enter. Escape
 * puts the saved note back. An emptied note is cleared. The saved note is what the store holds, so a draft that differs from it
 * after a save has finished is one that failed.
 */
const SuspenseNote = ({
  txId,
  note,
  updatedBy,
  updatedAt,
  multiline = false,
  placeholder = 'Add a note...',
}: SuspenseNoteProps) => {
  const dispatch = useDispatch()
  const saved = note ?? ''
  const [draft, setDraft] = useState(saved)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const isSaving = useSelector(state => apiStateSelectors.isLoading(state)([walletApiEvents.saveSuspenseNote_request]))

  // Pick up the note when it changes underneath, e.g. a refetch or this save landing, but leave
  // an edit in progress alone.
  const previousSaved = useRef(saved)
  useEffect(() => {
    setDraft(current => (current === previousSaved.current || current.trim() === saved ? saved : current))
    if (draft.trim() === saved) setHasSubmitted(false)
    previousSaved.current = saved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved])

  const isDirty = draft.trim() !== saved

  const save = (): void => {
    if (!txId || !isDirty) return
    setHasSubmitted(true)
    dispatch(walletApiEvents.saveSuspenseNote_request({ params: { txId }, body: { notes: draft.trim() || null } }))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.key === 'Escape') {
      setDraft(saved)
      setHasSubmitted(false)
    } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      save()
    }
  }

  const inputProps = {
    className: styles.input,
    value: draft,
    placeholder,
    maxLength: SUSPENSE_NOTE_MAX_LENGTH,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(e.target.value),
    onBlur: multiline ? undefined : save,
    onKeyDown: handleKeyDown,
  }

  let status: React.ReactNode = null
  if (hasSubmitted && isDirty) {
    status = isSaving ? 'Saving…' : <span className={styles.error}>Not saved. Press Enter to try again.</span>
  } else if (saved && (updatedBy || updatedAt)) {
    status = [updatedBy, updatedAt && formatStandard(updatedAt)].filter(Boolean).join(' · ')
  }

  return (
    // The row opens the detail panel on click; typing in a note shouldn't.
    <div className={cx(styles.root, { [styles.multiline]: multiline })} onClick={e => e.stopPropagation()}>
      {multiline ? <textarea rows={3} {...inputProps} /> : <input type="text" {...inputProps} />}
      {multiline ? (
        <div className={styles.footer}>
          <small className={styles.status}>{status}</small>
          <Button size="sm" secondary disabled={!isDirty || isSaving} onClick={save}>
            Save note
          </Button>
        </div>
      ) : (
        status && <small className={styles.status}>{status}</small>
      )}
    </div>
  )
}

export default SuspenseNote
